import React from "react";
import { gql, useQuery } from "@apollo/client";
import { withStyles } from "@material-ui/core/styles";
import TablePagination from "@material-ui/core/TablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TableSortLabel,
  Typography
} from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";

const styles = theme => ({
  root: {
    marginTop: theme.spacing(3),
    overflowX: "auto",
    margin: "auto"
  },
  table: {
    minWidth: 700
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 300
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  }
});

function PlayerSplitList(props) {
  const { classes, playerId } = props;
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("points");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [split, setSplit] = React.useState("WinLoss");
  const { loading, data, error } = useQuery(
    gql`
    query playerSplitQuery(
      $id: Int
      $first: Int
      $offset: Int
      $orderBy: [_PlayerSplitOrdering]
    ) {
      Player${split}SplitById(id: $id,first: $first, offset: $offset, orderBy: $orderBy) {
        category,
        games,
        powers,
        points,
        gets,
        negs,
        tossups_heard,
        pp20tuh
      }
    }
  `,
    {
      variables: {
        id: playerId,
        first: rowsPerPage,
        offset: rowsPerPage * page,
        orderBy: orderBy + "_" + order
      }
    }
  );

  const handleChangePage = (_, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = (_, rows) => {
    setRowsPerPage(parseInt(rows.key));
  };

  const handleSortRequest = property => {
    const newOrderBy = property;
    let newOrder = "desc";

    if (orderBy === property && order === "desc") {
      newOrder = "asc";
    }

    setOrder(newOrder);
    setOrderBy(newOrderBy);
  };

  const handleSplitChange = event => {
    setSplit(event.target.value);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        Splits
      </Typography>
      <FormControl className={classes.formControl}>
        <Select value={split} onChange={handleSplitChange}>
          <MenuItem value={"WinLoss"}>Win/Loss</MenuItem>
          <MenuItem value={"Teammate"}>Player Teammate</MenuItem>
          <MenuItem value={"Team"}>Player Team</MenuItem>
          <MenuItem value={"School"}>Player School</MenuItem>
          <MenuItem value={"OpponentTeam"}>Opponent Team</MenuItem>
          <MenuItem value={"OpponentSchool"}>Opponent School</MenuItem>
          <MenuItem value={"Provider"}>Question Provider</MenuItem>
          <MenuItem value={"Round"}>Round</MenuItem>
          <MenuItem value={"Year"}>Year</MenuItem>
          <MenuItem value={"Month"}>Month</MenuItem>
          <MenuItem value={"Online"}>Online / In-Person</MenuItem>
        </Select>
      </FormControl>
      {loading && !error && <p>Loading...</p>}
      {error && !loading && <p>Error</p>}
      {data && !loading && !error && (
        <div>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell
                  key="category"
                  sortDirection={orderBy === "category" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "category"}
                      direction={order}
                      onClick={() => handleSortRequest("category")}
                    >
                      Category
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="games"
                  sortDirection={orderBy === "games" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "games"}
                      direction={order}
                      onClick={() => handleSortRequest("games")}
                    >
                      Games
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="powers"
                  sortDirection={orderBy === "powers" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "powers"}
                      direction={order}
                      onClick={() => handleSortRequest("powers")}
                    >
                      Powers
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="gets"
                  sortDirection={orderBy === "gets" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "gets"}
                      direction={order}
                      onClick={() => handleSortRequest("gets")}
                    >
                      Gets
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="negs"
                  sortDirection={orderBy === "negs" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "negs"}
                      direction={order}
                      onClick={() => handleSortRequest("negs")}
                    >
                      Negs
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="points"
                  sortDirection={orderBy === "points" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "points"}
                      direction={order}
                      onClick={() => handleSortRequest("points")}
                    >
                      Points
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key="pp20tuh"
                  sortDirection={orderBy === "pp20tuh" ? order : false}
                >
                  <Tooltip
                    title="Sort"
                    placement="bottom-start"
                    enterDelay={300}
                  >
                    <TableSortLabel
                      active={orderBy === "pp20tuh"}
                      direction={order}
                      onClick={() => handleSortRequest("pp20tuh")}
                    >
                      PP20TUH
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data[`Player${split}SplitById`].map(n => {
                return (
                  <TableRow key={n.category}>
                    <TableCell component="th" scope="row">
                      {split === "Month"
                        ? new Date(
                            new Date().setMonth(n.category)
                          ).toLocaleString("default", { month: "long" })
                        : n.category}
                    </TableCell>
                    <TableCell>{n.games}</TableCell>
                    <TableCell>{n.powers}</TableCell>
                    <TableCell>{n.gets}</TableCell>
                    <TableCell>{n.negs}</TableCell>
                    <TableCell>{n.points?.toLocaleString()}</TableCell>
                    <TableCell>{n.pp20tuh?.toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={Number.MAX_VALUE}
            labelDisplayedRows={({ from, to }) => `Results ${from} - ${to}`}
            rowsPerPage={rowsPerPage}
            nextIconButtonProps={
              data[`Player${split}SplitById`].length < rowsPerPage
                ? { disabled: true }
                : {}
            }
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </div>
      )}
    </div>
  );
}

export default withStyles(styles)(PlayerSplitList);
